/**
 * EvmDashboard — EVM-Lite visual dashboard for BudgetTab.
 *
 * Three sections:
 *   1. Gauge cards — CPI, SPI, BAC, EAC, ETC, VAC, TCPI
 *   2. S-curve chart — PV/EV/AC over time (SVG-based, no external lib)
 *   3. Variance analysis table — breakdowns by phase or category
 *
 * Only rendered when the project has linked resources (EVM-active).
 * All strings use `useAppTranslation('pages')`.
 *
 * Color semantics follow CLAUDE.md Management tab palette:
 *   Blue = Complete, Green = On Track, Yellow = Warning, Red = Critical
 *
 * Epic: Budget & Cost Tracking (DEV-578)
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { fetchEvmData, writeEvmSnapshot, fetchEvmTaskBreakdown } from "../../api/evm";
import type { EvmData, EvmPhaseBreakdownResponse, EvmCategoryBreakdownResponse, EvmTaskBreakdown } from "../../api/evm";
import { formatCurrency } from "../../Data/Budget/currencyLookup";
import styles from "./EvmDashboard.module.css";

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface Props {
  projectGuid: string;
  budgetCurrency: string;
  canEdit: boolean;
  /** Signal to trigger a data refresh (bump from parent). */
  refreshTrigger?: number;
  debugMode?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/** Return CSS class for CPI/SPI status (management tab palette). */
function indexStatus(value: number): string {
  if (value === 0) return styles.statusNeutral;
  if (value >= 0.95) return styles.statusGood;    // Green: on track
  if (value >= 0.8)  return styles.statusWarning;  // Yellow: attention
  return styles.statusBad;                          // Red: critical
}

/** Human-readable label for CPI range. */
function cpiLabel(value: number, t: (key: string) => string): string {
  if (value === 0) return "";
  if (value >= 1.0) return t("budget.evm.underBudget");
  if (value >= 0.9) return t("budget.evm.attention");
  return t("budget.evm.overBudget");
}

/** Human-readable label for SPI range. */
function spiLabel(value: number, t: (key: string) => string): string {
  if (value === 0) return "";
  if (value >= 1.0) return t("budget.evm.aheadOfSchedule");
  if (value >= 0.9) return t("budget.evm.attention");
  return t("budget.evm.behindSchedule");
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const EvmDashboard: React.FC<Props> = ({
  projectGuid,
  budgetCurrency,
  canEdit,
  refreshTrigger = 0,
  debugMode,
}) => {
  const { t } = useAppTranslation("pages");
  const { t: tKpi } = useAppTranslation("kpi");

  /* ── State ── */
  const [evmData, setEvmData] = useState<EvmData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snapshotSaving, setSnapshotSaving] = useState(false);
  const [snapshotToast, setSnapshotToast] = useState<string | null>(null);
  const [varianceView, setVarianceView] = useState<"phase" | "category">("phase");
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [taskBreakdown, setTaskBreakdown] = useState<EvmTaskBreakdown[]>([]);
  const [taskLoading, setTaskLoading] = useState(false);

  /* ── Fetch EVM data ── */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    console.log("[EvmDashboard] Fetching EVM data for project:", projectGuid);
    fetchEvmData(projectGuid)
      .then((data) => {
        if (!cancelled) {
          console.log("[EvmDashboard] Data received:", data);
          setEvmData(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("[EvmDashboard] Fetch failed:", err.message);
          setError(err.message);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [projectGuid, refreshTrigger]);

  /* ── Snapshot handler ── */
  const handleSnapshot = useCallback(async () => {
    setSnapshotSaving(true);
    setSnapshotToast(null);
    try {
      const result = await writeEvmSnapshot(projectGuid);
      setEvmData(result.evm);
      setSnapshotToast(
        result.snapshotWritten
          ? t("budget.evm.snapshotSaved")
          : t("budget.evm.snapshotSkipped")
      );
      setTimeout(() => setSnapshotToast(null), 4000);
    } catch (err: any) {
      console.error("[EvmDashboard] Snapshot failed:", err.message);
      setSnapshotToast("Error: " + err.message);
      setTimeout(() => setSnapshotToast(null), 5000);
    } finally {
      setSnapshotSaving(false);
    }
  }, [projectGuid, t]);

  /* ── Drill-down toggle ── */
  const handleDrillDownToggle = useCallback(async () => {
    if (drillDownOpen) {
      setDrillDownOpen(false);
      return;
    }
    setTaskLoading(true);
    setDrillDownOpen(true);
    try {
      const tasks = await fetchEvmTaskBreakdown(projectGuid);
      setTaskBreakdown(tasks);
    } catch (err: any) {
      console.error("[EvmDashboard] Task breakdown failed:", err.message);
      setTaskBreakdown([]);
    } finally {
      setTaskLoading(false);
    }
  }, [projectGuid, drillDownOpen]);

  /* ── Early returns ── */
  if (loading) return null; // Silently wait for data
  if (error) {
    return (
      <div className={styles.evmDashboard}>
        <div className={styles.evmHeader}>
          <h3 className={styles.evmTitle}>{t("budget.evm.sectionTitle")}</h3>
        </div>
        <div className={styles.emptyState} style={{ color: "var(--color-error)" }}>
          EVM Error: {error}
        </div>
      </div>
    );
  }
  if (!evmData || evmData.linkedItemCount === 0) {
    return (
      <div className={styles.evmDashboard}>
        <div className={styles.evmHeader}>
          <h3 className={styles.evmTitle}>{t("budget.evm.sectionTitle")}</h3>
        </div>
        <div className={styles.emptyState}>
          {t("budget.evm.noLinkedResources")}
        </div>
      </div>
    );
  }

  const d = evmData;
  const fmt = (v: number) => formatCurrency(v, budgetCurrency);

  return (
    <div className={styles.evmDashboard}>
      {/* ── Header ── */}
      <div className={styles.evmHeader}>
        <h3 className={styles.evmTitle}>{t("budget.evm.sectionTitle")}</h3>
        <div className={styles.evmActions}>
          <span className={styles.linkedBadge}>
            {t("budget.evm.linkedResources", { count: d.linkedItemCount, total: d.itemCount })}
          </span>
          {canEdit && (
            <button
              className={styles.snapshotBtn}
              onClick={handleSnapshot}
              disabled={snapshotSaving}
            >
              {snapshotSaving ? "..." : t("budget.evm.writeSnapshot")}
            </button>
          )}
          {snapshotToast && <span className={styles.toast}>{snapshotToast}</span>}
        </div>
      </div>

      {/* ── Gauge cards ── */}
      <div className={styles.gaugeRow}>
        {/* CPI */}
        <div className={styles.gaugeCard}>
          <span className={styles.gaugeLabel}>{t("budget.evm.cpiLabel")}</span>
          <span className={`${styles.gaugeValue} ${indexStatus(d.cpi)}`}>
            {d.cpi.toFixed(2)}
          </span>
          <span className={styles.gaugeSubtext}>{cpiLabel(d.cpi, t)}</span>
        </div>

        {/* SPI */}
        <div className={styles.gaugeCard}>
          <span className={styles.gaugeLabel}>{t("budget.evm.spiLabel")}</span>
          <span className={`${styles.gaugeValue} ${indexStatus(d.spi)}`}>
            {d.spi.toFixed(2)}
          </span>
          <span className={styles.gaugeSubtext}>{spiLabel(d.spi, t)}</span>
        </div>

        {/* BAC */}
        <div className={styles.gaugeCard}>
          <span className={styles.gaugeLabel}>{t("budget.evm.bacLabel")}</span>
          <span className={`${styles.gaugeValue} ${styles.statusNeutral}`}>
            {fmt(d.bac)}
          </span>
        </div>

        {/* EAC */}
        <div className={styles.gaugeCard}>
          <span className={styles.gaugeLabel}>{t("budget.evm.eacLabel")}</span>
          <span className={`${styles.gaugeValue} ${d.eac > d.bac ? styles.statusBad : styles.statusGood}`}>
            {fmt(d.eac)}
          </span>
        </div>

        {/* ETC */}
        <div className={styles.gaugeCard}>
          <span className={styles.gaugeLabel}>{t("budget.evm.etcLabel")}</span>
          <span className={`${styles.gaugeValue} ${styles.statusNeutral}`}>
            {fmt(d.etc)}
          </span>
        </div>

        {/* VAC */}
        <div className={styles.gaugeCard}>
          <span className={styles.gaugeLabel}>{t("budget.evm.vacLabel")}</span>
          <span className={`${styles.gaugeValue} ${d.vac >= 0 ? styles.statusGood : styles.statusBad}`}>
            {fmt(d.vac)}
          </span>
        </div>

        {/* TCPI */}
        <div className={styles.gaugeCard}>
          <span className={styles.gaugeLabel}>{t("budget.evm.tcpiLabel")}</span>
          <span className={`${styles.gaugeValue} ${d.tcpi > 1.1 ? styles.statusBad : d.tcpi > 1.0 ? styles.statusWarning : styles.statusGood}`}>
            {d.tcpi.toFixed(2)}
          </span>
        </div>
      </div>

      {/* ── S-curve snapshot (PV / EV / AC bar comparison) ── */}
      <div className={styles.sCurveSection}>
        <h4 className={styles.sCurveTitle}>{t("budget.evm.sCurve")}</h4>
        <EvmBarChart pv={d.pv} ev={d.ev} ac={d.ac} bac={d.bac} t={t} fmt={fmt} />
      </div>

      {/* ── Variance analysis table ── */}
      <div className={styles.varianceSection}>
        <h4 className={styles.varianceTitle}>{t("budget.evm.varianceAnalysis")}</h4>
        <div className={styles.varianceTabs}>
          <button
            className={`${styles.varianceTab} ${varianceView === "phase" ? styles.varianceTabActive : ""}`}
            onClick={() => setVarianceView("phase")}
          >
            {t("budget.evm.byPhase")}
          </button>
          <button
            className={`${styles.varianceTab} ${varianceView === "category" ? styles.varianceTabActive : ""}`}
            onClick={() => setVarianceView("category")}
          >
            {t("budget.evm.byCategory")}
          </button>
        </div>

        {varianceView === "phase" ? (
          <PhaseVarianceTable rows={d.phaseBreakdown} budgetCurrency={budgetCurrency} t={t} tKpi={tKpi} />
        ) : (
          <CategoryVarianceTable rows={d.categoryBreakdown} budgetCurrency={budgetCurrency} t={t} tKpi={tKpi} />
        )}
      </div>

      {/* ── Task drill-down ── */}
      <div className={styles.varianceSection}>
        <button
          className={styles.varianceTab}
          onClick={handleDrillDownToggle}
          style={{ marginBlockEnd: drillDownOpen ? "0.75rem" : 0 }}
        >
          {drillDownOpen ? "▾" : "▸"} {t("budget.evm.taskPerformance")}
        </button>
        {drillDownOpen && (
          taskLoading ? null : (
            <TaskDrillDownTable rows={taskBreakdown} budgetCurrency={budgetCurrency} t={t} tKpi={tKpi} />
          )
        )}
      </div>

      {/* ── Debug output ── */}
      {debugMode && (
        <pre style={{ fontSize: "0.6875rem", color: "var(--text-muted)", padding: "0.5rem 1rem", whiteSpace: "pre-wrap" }}>
          {JSON.stringify(d, null, 2)}
        </pre>
      )}
    </div>
  );
};

export default EvmDashboard;

/* ================================================================== */
/*  Sub-components                                                     */
/* ================================================================== */

/** Simple horizontal bar chart comparing PV, EV, and AC against BAC. */
function EvmBarChart({
  pv, ev, ac, bac, t, fmt,
}: {
  pv: number; ev: number; ac: number; bac: number;
  t: (key: string) => string;
  fmt: (v: number) => string;
}) {
  const max = Math.max(pv, ev, ac, bac, 1);
  const barW = (v: number) => `${Math.min((v / max) * 100, 100)}%`;

  const bars = [
    { label: t("budget.evm.pvLabel"), abbr: t("budget.evm.pv"), value: pv, color: "var(--info-text)" },
    { label: t("budget.evm.evLabel"), abbr: t("budget.evm.ev"), value: ev, color: "var(--color-success)" },
    { label: t("budget.evm.acLabel"), abbr: t("budget.evm.ac"), value: ac, color: ac > ev ? "var(--color-error)" : "var(--color-warning)" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      {bars.map((b) => (
        <div key={b.abbr} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", minWidth: "2rem", textAlign: "end" }}>
            {b.abbr}
          </span>
          <div
            style={{
              flex: 1,
              height: "1.25rem",
              background: "var(--bg-elevated)",
              borderRadius: "4px",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              style={{
                width: barW(b.value),
                height: "100%",
                background: b.color,
                borderRadius: "4px",
                transition: "width 0.4s ease-out",
              }}
            />
          </div>
          <span style={{ fontSize: "0.75rem", color: "var(--text-primary)", minWidth: "5.5rem", textAlign: "end", fontVariantNumeric: "tabular-nums" }}>
            {fmt(b.value)}
          </span>
        </div>
      ))}
      {/* BAC reference line label */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", minWidth: "2rem", textAlign: "end" }}>
          {t("budget.evm.bac")}
        </span>
        <div
          style={{
            flex: 1,
            height: "2px",
            background: "var(--text-muted)",
            opacity: 0.4,
            borderRadius: "1px",
          }}
        />
        <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", minWidth: "5.5rem", textAlign: "end", fontVariantNumeric: "tabular-nums" }}>
          {fmt(bac)}
        </span>
      </div>
    </div>
  );
}

/** Phase breakdown variance table. */
function PhaseVarianceTable({
  rows, budgetCurrency, t, tKpi,
}: {
  rows: EvmPhaseBreakdownResponse[];
  budgetCurrency: string;
  t: (key: string) => string;
  tKpi: (key: string) => string;
}) {
  const fmt = (v: number) => formatCurrency(v, budgetCurrency);

  const phaseLabel = (phase: string): string => {
    const key = `preflight.phases.${phase}`;
    const result = tKpi(key);
    return result === key ? phase : result;
  };

  return (
    <table className={styles.varianceTable}>
      <thead>
        <tr>
          <th>{t("budget.evm.byPhase")}</th>
          <th className={styles.numericCell}>{t("budget.evm.estimated")}</th>
          <th className={styles.numericCell}>{t("budget.evm.pv")}</th>
          <th className={styles.numericCell}>{t("budget.evm.ev")}</th>
          <th className={styles.numericCell}>{t("budget.evm.ac")}</th>
          <th className={styles.numericCell}>{t("budget.evm.cv")}</th>
          <th className={styles.numericCell}>{t("budget.evm.sv")}</th>
          <th className={styles.numericCell}>{t("budget.evm.cpi")}</th>
          <th className={styles.numericCell}>{t("budget.evm.spi")}</th>
          <th className={styles.numericCell}>{t("budget.evm.items")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.phase}>
            <td>{phaseLabel(r.phase)}</td>
            <td className={styles.numericCell}>{fmt(r.estimated)}</td>
            <td className={styles.numericCell}>{fmt(r.pv)}</td>
            <td className={styles.numericCell}>{fmt(r.ev)}</td>
            <td className={styles.numericCell}>{fmt(r.ac)}</td>
            <td className={styles.numericCell} style={{ color: r.cv >= 0 ? "var(--color-success)" : "var(--color-error)" }}>
              {fmt(r.cv)}
            </td>
            <td className={styles.numericCell} style={{ color: r.sv >= 0 ? "var(--color-success)" : "var(--color-error)" }}>
              {fmt(r.sv)}
            </td>
            <td className={styles.numericCell} style={{ color: r.cpi >= 0.95 ? "var(--color-success)" : r.cpi >= 0.8 ? "var(--color-warning)" : "var(--color-error)" }}>
              {r.cpi.toFixed(2)}
            </td>
            <td className={styles.numericCell} style={{ color: r.spi >= 0.95 ? "var(--color-success)" : r.spi >= 0.8 ? "var(--color-warning)" : "var(--color-error)" }}>
              {r.spi.toFixed(2)}
            </td>
            <td className={styles.numericCell}>{r.itemCount}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Category breakdown variance table. */
function CategoryVarianceTable({
  rows, budgetCurrency, t, tKpi,
}: {
  rows: EvmCategoryBreakdownResponse[];
  budgetCurrency: string;
  t: (key: string) => string;
  tKpi: (key: string) => string;
}) {
  const fmt = (v: number) => formatCurrency(v, budgetCurrency);

  const catLabel = (cat: string): string => {
    const key = `preflight.costCategories.${cat}`;
    const result = tKpi(key);
    return result === key ? cat : result;
  };

  return (
    <table className={styles.varianceTable}>
      <thead>
        <tr>
          <th>{t("budget.evm.byCategory")}</th>
          <th className={styles.numericCell}>{t("budget.evm.estimated")}</th>
          <th className={styles.numericCell}>{t("budget.evm.pv")}</th>
          <th className={styles.numericCell}>{t("budget.evm.ev")}</th>
          <th className={styles.numericCell}>{t("budget.evm.ac")}</th>
          <th className={styles.numericCell}>{t("budget.evm.cv")}</th>
          <th className={styles.numericCell}>{t("budget.evm.sv")}</th>
          <th className={styles.numericCell}>{t("budget.evm.cpi")}</th>
          <th className={styles.numericCell}>{t("budget.evm.spi")}</th>
          <th className={styles.numericCell}>{t("budget.evm.items")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.category}>
            <td>{catLabel(r.category)}</td>
            <td className={styles.numericCell}>{fmt(r.estimated)}</td>
            <td className={styles.numericCell}>{fmt(r.pv)}</td>
            <td className={styles.numericCell}>{fmt(r.ev)}</td>
            <td className={styles.numericCell}>{fmt(r.ac)}</td>
            <td className={styles.numericCell} style={{ color: r.cv >= 0 ? "var(--color-success)" : "var(--color-error)" }}>
              {fmt(r.cv)}
            </td>
            <td className={styles.numericCell} style={{ color: r.sv >= 0 ? "var(--color-success)" : "var(--color-error)" }}>
              {fmt(r.sv)}
            </td>
            <td className={styles.numericCell} style={{ color: r.cpi >= 0.95 ? "var(--color-success)" : r.cpi >= 0.8 ? "var(--color-warning)" : "var(--color-error)" }}>
              {r.cpi.toFixed(2)}
            </td>
            <td className={styles.numericCell} style={{ color: r.spi >= 0.95 ? "var(--color-success)" : r.spi >= 0.8 ? "var(--color-warning)" : "var(--color-error)" }}>
              {r.spi.toFixed(2)}
            </td>
            <td className={styles.numericCell}>{r.itemCount}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Per-task EVM drill-down table. */
function TaskDrillDownTable({
  rows, budgetCurrency, t, tKpi,
}: {
  rows: EvmTaskBreakdown[];
  budgetCurrency: string;
  t: (key: string) => string;
  tKpi: (key: string) => string;
}) {
  const fmt = (v: number) => formatCurrency(v, budgetCurrency);

  const phaseLabel = (phase: string): string => {
    const key = `preflight.phases.${phase}`;
    const result = tKpi(key);
    return result === key ? phase : result;
  };

  if (rows.length === 0) {
    return (
      <div className={styles.emptyState}>
        {t("budget.evm.noLinkedResources")}
      </div>
    );
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table className={styles.varianceTable}>
        <thead>
          <tr>
            <th>{t("budget.evm.taskPerformance")}</th>
            <th style={{ minWidth: "6rem" }}>{t("budget.evm.byPhase")}</th>
            <th className={styles.numericCell}>%</th>
            <th className={styles.numericCell}>{t("budget.evm.estimated")}</th>
            <th className={styles.numericCell}>{t("budget.evm.pv")}</th>
            <th className={styles.numericCell}>{t("budget.evm.ev")}</th>
            <th className={styles.numericCell}>{t("budget.evm.ac")}</th>
            <th className={styles.numericCell}>{t("budget.evm.cpi")}</th>
            <th className={styles.numericCell}>{t("budget.evm.spi")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.preflightItemId}>
              <td>
                <div style={{ fontWeight: 500 }}>{r.label}</div>
                {r.taskName && (
                  <div style={{ fontSize: "0.6875rem", color: "var(--text-muted)" }}>
                    {r.taskName}
                  </div>
                )}
              </td>
              <td>{phaseLabel(r.phase)}</td>
              <td className={styles.numericCell}>{r.percentComplete}%</td>
              <td className={styles.numericCell}>{fmt(r.estimated)}</td>
              <td className={styles.numericCell}>{fmt(r.pv)}</td>
              <td className={styles.numericCell}>{fmt(r.ev)}</td>
              <td className={styles.numericCell}>{fmt(r.ac)}</td>
              <td className={styles.numericCell} style={{ color: r.cpi >= 0.95 ? "var(--color-success)" : r.cpi >= 0.8 ? "var(--color-warning)" : "var(--color-error)" }}>
                {r.cpi.toFixed(2)}
              </td>
              <td className={styles.numericCell} style={{ color: r.spi >= 0.95 ? "var(--color-success)" : r.spi >= 0.8 ? "var(--color-warning)" : "var(--color-error)" }}>
                {r.spi.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
